// Unpublished Work (c) 2023-2024 Deere & Company.
import { GridColDef } from "@mui/x-data-grid-pro";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { type TabName } from "./OrdersByLineItem/reducers/actions";
import {
  Address,
  countryCurrencyMap,
  countryCurrencySymbolMap,
  DEFAULT_COUNTRY_CODE,
  DEFAULT_COUNTRY_CURRENCY_SYMBOL,
  Line,
  LinePromotion,
  LineState,
  LineStatus,
  M2NOOutboundTransactionStatus,
  NewServiceLevelAgreement,
  Order,
  OrderClient,
  OrderLineItem,
  OrderServiceType,
  OrderStatus,
  OrderType,
  salesOrgCurrencyMap,
  salesOrgCurrencySymbolMap,
  SubLine,
} from "./types";
import {
  LineItem,
  Order as OldOrder,
  SavedAddress,
  SubLineItem,
} from "../../api/orders/orderInterfaces";
import { ADDITIONAL_FIELDS } from "../featureToggles/constants";

dayjs.extend(utc);

export const isToggleEnabledForOrderType = (
  isToggleEnabled: (category: string, toggle: string) => boolean,
  additionalFieldsValues:
    | ((category: string, toggle: string) => Map<string, string[]> | undefined)
    | undefined,
  category: string,
  toggle: string,
  orderType: string | undefined
): boolean => {
  if (!isToggleEnabled(category, toggle)) return false;

  const orderTypes = additionalFieldsValues?.(category, toggle)?.get(
    ADDITIONAL_FIELDS.ORDER_TYPE
  );

  return (
    !orderTypes ||
    orderTypes.length === 0 ||
    orderTypes.includes((orderType ?? "").toLowerCase())
  );
};

// ------ createOrderLineItemsFromNewOrderType - Start ------
const createOrderLineItemFromSubLine = (
  line: Line,
  subLine: SubLine,
  childLine: boolean = false
): OrderLineItem => {
  const toNumber: number = +subLine.lineNumber;

  const nonCancelledLineCount = () =>
    line.subLines.filter((value) => {
      if (value.lineStatus?.includes("CANCELLED")) {
        return false;
      }
      return !value.cancellationDetail?.isRequestedForCancellation;
    }).length;

  return {
    lineNumber: [line.lineNumber, ...(childLine ? [+toNumber] : [])],
    externalParentLineNumber: line.externalLineNumber,
    totalNonCancelledSubLines: nonCancelledLineCount(),
    scheduleLineNumber: subLine.lineNumber,
    lineId: childLine ? `${line.lineNumber}-${toNumber}` : line.id,
    parentLineId: line.id,
    lineType: "scheduledLines",
    bin: line.bin,
    quantity: subLine.quantity,
    parentQuantity: line.quantity,
    partNumber: subLine.partNumber,
    state: line.state,
    parentPartNumber: line.partNumber,
    description: subLine.partDescription,
    deliveryNumber: subLine.deliveryNumber,
    effectivePrice: subLine.effectivePrice,
    shipUnit: subLine.shippingUnit,
    shipUnitText: subLine.shippingUnitText,
    promotion: { id: subLine.promotion?.id as string, description: "" },
    unsuccessfulValidations: [],
    investigations: [],
    warning: subLine.lineStatus?.toString(),
    serviceLevelRequested: subLine.serviceLevelAgreement,
    newServiceLevelRequested: subLine.newServiceLevelAgreement,
    serviceLevelAgreementDescription: subLine.serviceLevelAgreementDescription,
    carrier: subLine.carrier,
    cancellationDetail: subLine.cancellationDetail,
    status: subLine.lineStatus?.toString(),
    statusCodes: subLine.lineStatusCodes,
    action: "",
    promiseDate: subLine.promiseDate?.toISOString(),
    shipDate: subLine.shipDate?.toISOString(),
    externalOrderNumber: subLine.externalOrderNumber,
    vendorDirectPart: subLine.vendorDirectPart,
    isLineEligibleForM2NO: !!subLine.isScheduleLineEligibleForM2NO,
    m2noOutboundOrderStatus: subLine.m2noOutboundOrderStatus,
    isSubstituted: subLine.isSubstituted,
    shipDateTime: subLine.shipDateTime,
    engineSerialNumber: !childLine ? line.engineSerialNumber : undefined,
  };
};

const getParentOrderLineItem = (line: Line): OrderLineItem => ({
  lineNumber: [line.lineNumber],
  lineId: line.id,
  parentLineId: line.id,
  lineType: "parentLineItem",
  quantity: line.quantity,
  parentQuantity: line.quantity,
  partNumber: line.partNumber,
  parentPartNumber: line.partNumber,
  bin: line.bin,
  state: line.state,
  description:
    line.subLines?.filter(
      (subLine) => subLine.partNumber === line.partNumber
    )[0]?.partDescription ?? "",
  deliveryNumber: "",
  effectivePrice: "",
  shipUnit: "",
  promotion: {
    id: "",
    description: "",
  },
  unsuccessfulValidations: [],
  investigations: [],
  warning: "",
  status: line.lineStatus?.toString() || "",
  statusCodes: [],
  action: "",
  externalOrderNumber: line.externalOrderNumber,
  m2noOutboundOrderStatus: line.m2noOutboundOrderStatus,
  isSubstituted:
    line.subLines.length === 0 ? false : line.subLines[0].isSubstituted,
  engineSerialNumber: line.engineSerialNumber,
});

const isOnlyOneSubLinePresent = (line: Line) =>
  line.subLines &&
  line.subLines.length === 1 &&
  line.quantity === line.subLines[0].quantity &&
  line.partNumber.toUpperCase() === line.subLines[0].partNumber.toUpperCase();

export const createOrderLineItemsFromNewOrderType = (
  order: Order
): OrderLineItem[] =>
  order.lines.flatMap((line) => {
    const parentOrderLineItem = getParentOrderLineItem(line);

    if (!line.subLines)
      return {
        ...parentOrderLineItem,
        warning: line.lineStatus?.toString() ?? "",
        status: line.lineStatus?.toString() ?? "",
      };

    if (isOnlyOneSubLinePresent(line))
      return createOrderLineItemFromSubLine(line, line.subLines[0]);

    const childOrderLineItems = line.subLines.map((subLine) =>
      createOrderLineItemFromSubLine(line, subLine, true)
    );

    return [parentOrderLineItem, ...childOrderLineItems];
  });
// ------ createOrderLineItemsFromNewOrderType - End ------

const createNewServiceLevelAgreement = (
  subLine: SubLineItem
): NewServiceLevelAgreement => {
  const currentValueMap = new Map<string, string>();

  if (subLine.serviceLevelAgreementCode) {
    currentValueMap.set(
      subLine.serviceLevelAgreementCode,
      subLine.serviceLevelAgreementDescription ?? ""
    );
  }

  return {
    partNumber: subLine.confirmedPartNumber,
    shippingUnit: subLine.shippingUnit,
    currentValue: currentValueMap,
    applicableSLRList: new Map<string, string>(),
  };
};

// ------ convertToNewOrderType - Start ------
const convertSubLineToNewSubLine = (subLine: SubLineItem): SubLine => ({
  lineNumber: subLine.scheduleLineNumber,
  partDescription: subLine.confirmedPartDescription,
  partNumber: subLine.confirmedPartNumber,
  quantity: subLine.confirmedQuantity,
  shippingUnit: subLine.shippingUnit,
  shippingUnitText: subLine.shippingUnitText,
  lineStatus: LineStatus[subLine.lineStatus as keyof typeof LineStatus],
  lineStatusCodes: subLine.lineStatusCodes,
  serviceLevelAgreement: {
    partNumber: subLine.confirmedPartNumber,
    shippingUnit: subLine.shippingUnit,
    currentValue: subLine.serviceLevelAgreement!,
    applicableSLRList: [],
  },
  newServiceLevelAgreement: createNewServiceLevelAgreement(subLine),
  serviceLevelAgreementDescription: subLine.serviceLevelAgreementDescription,
  carrier: { carrierValue: subLine.carrier },
  effectivePrice: subLine.effectivePrice,
  promotion: { id: subLine.promotionId, description: "" },
  promiseDate: subLine.promiseDate ? new Date(subLine.promiseDate) : undefined,
  shipDate: subLine.shipDate ? new Date(subLine.shipDate) : undefined,
  cancellationDetail: subLine.cancellationDetail,
  deliveryNumber: subLine.deliveryNumber,
  unsuccessfulValidations: subLine.unsuccessfulValidations,
  investigations: subLine.investigations,
  externalOrderNumber: subLine.externalOrderNumber,
  vendorDirectPart: subLine.vendorDirectPart,
  isScheduleLineEligibleForM2NO: subLine.isScheduleLineEligibleForM2NO,
  m2noOutboundOrderStatus:
    M2NOOutboundTransactionStatus[
      subLine.m2noOutboundOrderStatus as keyof typeof M2NOOutboundTransactionStatus
    ],
  isSubstituted: subLine.isSubstituted,
  shipDateTime: subLine.shipDateTime,
});

const convertLineToNewLineType = (line: LineItem): Line => ({
  lineNumber: line.lineNumber,
  externalLineNumber: line.externalLineNumber,
  id: line.lineId as string,
  partNumber: line.partNumber,
  quantity: line.quantity,
  state: LineState.SUBMITTED,
  lineStatus: LineStatus[line.lineStatus as keyof typeof LineStatus],
  bin: line.bin,
  serviceLevelAgreement: undefined,
  carrier: undefined,
  serviceLevelAgreementDescription: undefined,
  promotion: line.promotionId
    ? { id: line.promotionId, description: "" }
    : undefined,
  subLines: [...line.subLineItems]
    .sort((a, b) => a.scheduleLineNumber.localeCompare(b.scheduleLineNumber))
    .map(convertSubLineToNewSubLine),
  externalOrderNumber: line.externalOrderNumber,
  m2noOutboundOrderStatus: line.m2noOutboundOrderStatus,
  uniqueLineId: line.uniqueLineId,
  engineSerialNumber: line.engineSerialNumber,
});

export const convertOldAddressToNewAddressType = (
  address: SavedAddress | undefined
): Address | undefined => {
  if (address === undefined) return undefined;
  return {
    name: address.name,
    addressLine1: address.addressLineOne,
    addressLine2: address.addressLineTwo,
    city: address.cityName,
    state: address.stateOrProvinceName,
    country: address.countryCode,
    postalCode: address.zipCode,
    phoneNumber: address.phoneNumber,
    shipMode: address.shipMode,
  };
};

export const convertNewAddressToOldAddressType = (
  address: Address | undefined
): SavedAddress | undefined => {
  if (address === undefined) return undefined;
  return {
    name: address.name,
    addressLineOne: address.addressLine1,
    addressLineTwo: address.addressLine2,
    cityName: address.city,
    stateOrProvinceName: address.state,
    countryCode: address.country,
    zipCode: address.postalCode,
    phoneNumber: address.phoneNumber,
    shipMode: address.shipMode,
  };
};

export const convertToNewOrderType = (order: OldOrder): Order => ({
  dealerLocation: order.countryCode,
  accountId: order.accountId,
  orderNumber: order.orderNumber,
  countryCode: order.countryCode,
  ...(order.currencyCode ? { currencyCode: order.currencyCode } : {}),
  orderType: OrderType[order.orderType as keyof typeof OrderType],
  orderStatus: OrderStatus[order.orderStatus as keyof typeof OrderStatus],
  orderServiceType:
    OrderServiceType[order.orderServiceType as keyof typeof OrderServiceType],
  orderConsolidation: order.orderConsolidation,
  dealerReference: order.dealerReference,
  orderNotes: order.orderNotes,
  submittedBy: order.submittedBy,
  lastUpdatedBy: order.lastUpdatedBy || order.audit.updatedBy,
  submittedAt: order.submittedAt ? new Date(order.submittedAt) : undefined,
  externalOrderNumber: order.externalOrderNumber,
  generatedFrom: order.generatedFrom,
  lastUpdatedAt: undefined,
  lines: order.lineItems.map(convertLineToNewLineType),
  shipToAddress: convertOldAddressToNewAddressType(order.shipToAddress),
  soldToAddress: convertOldAddressToNewAddressType(order.soldToAddress),
  client: OrderClient[order.client as keyof typeof OrderClient],
  isShipToAddressOverridden: order.isShipToAddressOverridden,
  outboundAutoSubmit: order.outboundAutoSubmit,
  requestedShipMonth: order.shipDate ? dayjs(order.shipDate) : undefined,
  discount: order.discount,
  discountDescription: order.discountDescription,
  inProcessAsync: order.inProcessAsync,
});
// ------ convertToNewOrderType - End ------

export const collectAllLinePromotions = (lines: Line[]): LinePromotion[] =>
  lines.flatMap((line) =>
    line.subLines
      .filter((subLine) => subLine.promotion && subLine.promotion.id)
      .map((subLine) => ({
        id: subLine.promotion?.id as string,
        description: subLine.promotion?.description as string,
      }))
  );
export const countSubLinesWithoutPromotions = (lines: Line[]): number =>
  lines.flatMap((line) =>
    line.subLines.filter(
      (subLine) => !(subLine.promotion && subLine.promotion.id)
    )
  ).length;

export const computeOrderPriceAsString = (
  order: Order,
  maskPrice: boolean
): string => {
  const totalPrice = computeOrderPrice(order);
  if (maskPrice) {
    const maskedValue = totalPrice.toFixed(2).length;
    return "*".repeat(maskedValue);
  }
  return totalPrice.toFixed(2);
};

const computeSubLinesPrice = (line: Line): number =>
  line.subLines.reduce((total, subLine) => {
    const cancelled =
      subLine.lineStatus === LineStatus.AUTO_CANCELLED ||
      subLine.lineStatus === LineStatus.USER_CANCELLED;
    const subLinePrice = +subLine.effectivePrice;
    return !Number.isNaN(subLinePrice) && !cancelled
      ? total + subLinePrice
      : total;
  }, 0);

const computeLinePrice = (line: Line): number => {
  if (line.subLines?.length && line.subLines.length > 0) {
    return computeSubLinesPrice(line);
  }
  const lineTotal = Number(line.totalEffectivePrice);
  return Number.isNaN(lineTotal) ? 0 : lineTotal;
};

export const computeOrderPrice = (order: Order): number => {
  const totalPrice = order.lines.reduce(
    (total, line) => total + computeLinePrice(line),
    0
  );
  return Number(totalPrice.toFixed(2));
};

export type Preference = {
  key: string;
  value: string;
  userOverridable: boolean;
};
export const isCodedPricePreferred = (
  userPreferences: Preference[]
): boolean => {
  let maskPrice = false;
  if (Array.isArray(userPreferences)) {
    maskPrice =
      userPreferences.find(
        (preference) => preference.key === "price_visibility_for_order_status"
      )?.value === "no_price";
  }
  return maskPrice;
};

export const createJDPointOrderLink = (
  JDPOINT_URL: string,
  jdpointOrderNumber: string,
  accountId: string
) =>
  `${JDPOINT_URL}/dns/loadOrderForStatus.con?orderNumberSearch=${jdpointOrderNumber}&accountID=${accountId}&isCallFromPITM=true`;

export const reorderColumns = (
  oldColumnsOrder: GridColDef[],
  oldIndex: number,
  targetIndex: number
): GridColDef[] => {
  const copy = oldColumnsOrder.slice();
  const [column] = copy.splice(oldIndex, 1);
  return [
    ...copy.slice(0, targetIndex),
    column,
    ...copy.slice(targetIndex, copy.length),
  ];
};

export const applyColumnOrder = (
  columns: GridColDef[],
  order: string[] | undefined
): GridColDef[] => {
  if (!order?.length) return columns;
  // Fields missing from the saved order (e.g. newly toggled columns) go last.
  const rank = (field: string) => {
    const index = order.indexOf(field);
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };
  return [...columns].sort((a, b) => rank(a.field) - rank(b.field));
};

export const format = (date: Dayjs | undefined): string | undefined =>
  date ? date.format("YYYY-MM-DDTHH:mm:ss[Z]") : undefined;

export const convertFromDateToUTC = (
  date: Dayjs | undefined
): Dayjs | undefined => (date ? date.startOf("day").utc() : undefined);

export const convertToDateToUTC = (
  date: Dayjs | undefined
): Dayjs | undefined => (date ? date.endOf("day").utc() : undefined);

export const getMaxDate = (date1: Dayjs, date2: Dayjs): Dayjs =>
  date1.isAfter(date2) ? date1 : date2;

export const getCountryCurrencyCode = (
  location: string | undefined,
  salesOrg?: string
): string => {
  const compositeKey = location && salesOrg ? `${location}-${salesOrg}` : "";
  if (compositeKey && salesOrgCurrencyMap.has(compositeKey)) {
    return salesOrgCurrencyMap.get(compositeKey) as string;
  }
  return location && countryCurrencyMap.has(location)
    ? (countryCurrencyMap.get(location) as string)
    : DEFAULT_COUNTRY_CODE;
};

export const getCountryCurrencySymbol = (
  location: string | undefined,
  salesOrg?: string
): string => {
  const compositeKey = location && salesOrg ? `${location}-${salesOrg}` : "";
  if (compositeKey && salesOrgCurrencySymbolMap.has(compositeKey)) {
    return salesOrgCurrencySymbolMap.get(compositeKey) as string;
  }
  return location && countryCurrencySymbolMap.has(location)
    ? (countryCurrencySymbolMap.get(location) as string)
    : DEFAULT_COUNTRY_CURRENCY_SYMBOL;
};

export const canDisplayOrderCurrency = (
  currencyCode: string | null | undefined,
  location: string | undefined,
  useOrderResponseCurrencyCode: boolean = false
): boolean =>
  !!location || (useOrderResponseCurrencyCode && !!currencyCode?.trim());

export const getOrderCurrencyCode = (
  currencyCode: string | null | undefined,
  location: string | undefined,
  salesOrg?: string,
  useOrderResponseCurrencyCode: boolean = false
): string => {
  const responseCurrencyCode = useOrderResponseCurrencyCode
    ? currencyCode?.trim()
    : undefined;
  return responseCurrencyCode || getCountryCurrencyCode(location, salesOrg);
};

export const getOrderCurrencySymbol = (
  currencyCode: string | null | undefined,
  location: string | undefined,
  salesOrg?: string,
  useOrderResponseCurrencyCode: boolean = false
): string => {
  const responseCurrencyCode = useOrderResponseCurrencyCode
    ? currencyCode?.trim()
    : undefined;
  if (!responseCurrencyCode)
    return getCountryCurrencySymbol(location, salesOrg);

  try {
    return (
      new Intl.NumberFormat("en", {
        style: "currency",
        currency: responseCurrencyCode,
        currencyDisplay: "narrowSymbol",
      })
        .formatToParts(0)
        .find((part) => part.type === "currency")?.value ?? responseCurrencyCode
    );
  } catch {
    return responseCurrencyCode;
  }
};

export const getTabNameFromIndex = (tabIndex: number): TabName => {
  const tabNames: Record<number, TabName> = {
    0: "AllLines",
    1: "Backorders",
    2: "OnHold",
    3: "Investigations",
    4: "SentToSupplier",
    5: "SentToFactory",
  };
  return tabNames[tabIndex] || "AllLines";
};

export const euDealerLegacyPriceEnabled = (
  customerGroup5: string | undefined,
  enabled: boolean
): boolean => enabled && /^(EU[1-9]|E10)$/i.test(customerGroup5 ?? "");

/** Returns true when the dealer's customerGroup5 falls into the EU CG5 range (EU1–EU9 or E10). */
export const isEuDealerCG5 = (customerGroup5: string | undefined): boolean =>
  /^(EU[1-9]|E10)$/i.test(customerGroup5 ?? "");

export const getEuDealerPriceFlags = (
  customerGroup5: string | undefined,
  isEuDealerLegacyPriceEnabled: boolean
): {
  euDealerCG5: boolean;
  euDealerLegacyPriceOn: boolean;
  euDealerPriceMasked: boolean;
} => {
  const euDealerCG5 = isEuDealerCG5(customerGroup5);
  // Reuse the already-computed euDealerCG5 instead of running the regex a second time
  const euDealerLegacyPriceOn = isEuDealerLegacyPriceEnabled && euDealerCG5;
  return {
    euDealerCG5,
    euDealerLegacyPriceOn,
    euDealerPriceMasked: euDealerCG5 && !euDealerLegacyPriceOn,
  };
};
